import {
  type AnimationClip,
  InterpolateDiscrete,
  InterpolateLinear,
  InterpolateSmooth,
  type KeyframeTrack,
  QuaternionKeyframeTrack
} from 'three'
import { confirm_action } from '../../lib/ConfirmationDialog.ts'
import { ModalDialog } from '../../lib/ModalDialog.ts'

/**
 * CurvesEditor
 *
 * 2D graph view of an animation clip's tracks. The user picks a bone +
 * property + component (for vector/quaternion tracks), sees the keyframe
 * values plotted vs time, and can:
 *  - drag individual keyframes vertically to adjust their value
 *  - switch the track's interpolation mode (Linear / Smooth / Step)
 *
 * Mutates the clip's existing tracks in place. Calls `on_change` after every
 * edit so the host can persist + rebind the mixer.
 *
 * Out of scope (intentional MVP limit): per-keyframe tangent handles,
 * horizontal time-dragging of keyframes, multi-component visualization,
 * adding/removing keyframes from this view (use the timeline scrubber's
 * delete-keyframe button for removal).
 */

export interface CurvesEditorOptions {
  container: HTMLElement
  clip: AnimationClip
  on_change: () => void
  /** Called when user changes the visible track in the dropdown. */
  on_track_changed?: (track_name: string) => void
}

interface PlotPoint {
  x: number  // canvas pixel x
  y: number  // canvas pixel y
  time: number
  value: number
  index: number  // index into the track's times array
}

const CANVAS_W = 700
const CANVAS_H = 320
const PADDING_L = 50
const PADDING_R = 20
const PADDING_T = 20
const PADDING_B = 40
const HANDLE_RADIUS = 5

export class CurvesEditor {
  private readonly container: HTMLElement
  private readonly clip: AnimationClip
  private readonly on_change: () => void
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private current_track_name: string = ''
  private current_component: number = 0  // 0 = x, 1 = y, 2 = z, 3 = w (for vec/quat)
  private dragging_point_index: number | null = null
  private cached_points: PlotPoint[] = []

  constructor (options: CurvesEditorOptions) {
    this.container = options.container
    this.clip = options.clip
    this.on_change = options.on_change
  }

  mount (): void {
    this.container.innerHTML = this.template()
    this.canvas = this.container.querySelector('canvas') as HTMLCanvasElement
    this.ctx = this.canvas.getContext('2d')
    this.bind_handlers()

    const first_track = this.clip.tracks[0]
    if (first_track !== undefined) {
      this.current_track_name = first_track.name
      this.update_dropdowns()
      this.render()
    } else {
      this.show_empty_state()
    }
  }

  unmount (): void {
    this.container.innerHTML = ''
    this.canvas = null
    this.ctx = null
  }

  private template (): string {
    return `
      <div style="display: flex; flex-direction: column; gap: 0.5rem;">
        <div style="display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;">
          <label style="display: flex; align-items: center; gap: 0.25rem;">
            <span>Track</span>
            <select id="curves-track-select" style="min-width: 220px;"></select>
          </label>
          <label style="display: flex; align-items: center; gap: 0.25rem;">
            <span>Component</span>
            <select id="curves-component-select"></select>
          </label>
          <label style="display: flex; align-items: center; gap: 0.25rem;">
            <span>Interpolation</span>
            <select id="curves-interpolation-select">
              <option value="linear">Linear</option>
              <option value="smooth">Smooth</option>
              <option value="step">Step</option>
            </select>
          </label>
        </div>
        <canvas width="${CANVAS_W}" height="${CANVAS_H}" style="display: block; background: #1a1a1a; border-radius: 4px; cursor: crosshair;"></canvas>
        <p style="margin: 0; font-size: 0.85em; opacity: 0.8;">Drag a keyframe (yellow dot) vertically to change its value. Right-click empty space to insert a keyframe; right-click a keyframe to delete it.</p>
      </div>
    `
  }

  private bind_handlers (): void {
    const track_select = this.container.querySelector<HTMLSelectElement>('#curves-track-select')
    const component_select = this.container.querySelector<HTMLSelectElement>('#curves-component-select')
    const interp_select = this.container.querySelector<HTMLSelectElement>('#curves-interpolation-select')

    track_select?.addEventListener('change', () => {
      this.current_track_name = track_select.value
      this.current_component = 0
      this.update_dropdowns()
      this.render()
    })
    component_select?.addEventListener('change', () => {
      this.current_component = parseInt(component_select.value, 10)
      this.render()
    })
    interp_select?.addEventListener('change', () => {
      this.set_interpolation(interp_select.value)
    })

    this.canvas?.addEventListener('pointerdown', (e) => this.handle_pointer_down(e))
    this.canvas?.addEventListener('pointermove', (e) => this.handle_pointer_move(e))
    this.canvas?.addEventListener('pointerup', (e) => this.handle_pointer_up(e))
    this.canvas?.addEventListener('pointercancel', () => { this.dragging_point_index = null })
    // Right-click context menu: delete a keyframe under the cursor, or insert
    // one at the clicked time/value. Suppress the browser context menu so
    // users can right-click anywhere in the plot without dropping out into
    // the OS menu.
    this.canvas?.addEventListener('contextmenu', (e) => this.handle_context_menu(e))
  }

  private update_dropdowns (): void {
    const track_select = this.container.querySelector<HTMLSelectElement>('#curves-track-select')
    if (track_select !== null) {
      track_select.innerHTML = this.clip.tracks.map(t =>
        `<option value="${t.name}"${t.name === this.current_track_name ? ' selected' : ''}>${t.name}</option>`
      ).join('')
    }
    const component_select = this.container.querySelector<HTMLSelectElement>('#curves-component-select')
    const track = this.get_current_track()
    if (component_select !== null && track !== null) {
      const size = track.getValueSize()
      const labels = size === 4 ? ['x', 'y', 'z', 'w'] : size === 3 ? ['x', 'y', 'z'] : ['v']
      component_select.innerHTML = labels.map((l, i) =>
        `<option value="${i}"${i === this.current_component ? ' selected' : ''}>${l}</option>`
      ).join('')
      component_select.disabled = size <= 1
    }
    const interp_select = this.container.querySelector<HTMLSelectElement>('#curves-interpolation-select')
    if (interp_select !== null && track !== null) {
      const interp = (track as KeyframeTrack & { getInterpolation?: () => number }).getInterpolation?.()
      interp_select.value = interp === InterpolateDiscrete ? 'step'
        : interp === InterpolateSmooth ? 'smooth'
        : 'linear'
    }
  }

  private set_interpolation (mode: string): void {
    const track = this.get_current_track()
    if (track === null) return
    if (track instanceof QuaternionKeyframeTrack && mode === 'smooth') {
      // Three.js uses cubic (Smooth) interpolation that doesn't preserve unit
      // length on quaternions; warn the user and fall back to linear.
      const note = this.container.querySelector<HTMLParagraphElement>('p')
      if (note !== null) {
        note.textContent = 'Smooth interpolation isn\'t supported for quaternion tracks (it breaks unit length). Falling back to Linear.'
      }
      mode = 'linear'
    }
    const setter = (track as KeyframeTrack & { setInterpolation?: (mode: number) => void }).setInterpolation
    if (setter !== undefined) {
      const num_mode = mode === 'step' ? InterpolateDiscrete
        : mode === 'smooth' ? InterpolateSmooth
        : InterpolateLinear
      setter.call(track, num_mode)
    }
    this.on_change()
  }

  private get_current_track (): KeyframeTrack | null {
    return this.clip.tracks.find(t => t.name === this.current_track_name) ?? null
  }

  private show_empty_state (): void {
    if (this.ctx === null) return
    this.ctx.fillStyle = '#1a1a1a'
    this.ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)
    this.ctx.fillStyle = '#888'
    this.ctx.font = '14px sans-serif'
    this.ctx.fillText('No tracks in this clip', PADDING_L, CANVAS_H / 2)
  }

  private render (): void {
    if (this.ctx === null) return
    const track = this.get_current_track()
    if (track === null) { this.show_empty_state(); return }

    const size = track.getValueSize()
    const n = track.times.length
    const ctx = this.ctx

    // Background
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)

    if (n === 0) { this.show_empty_state(); return }

    // Compute value range with a 10% margin.
    const values: number[] = new Array(n)
    for (let i = 0; i < n; i++) values[i] = track.values[i * size + this.current_component]
    let min_v = Infinity; let max_v = -Infinity
    for (const v of values) { if (v < min_v) min_v = v; if (v > max_v) max_v = v }
    if (min_v === max_v) { min_v -= 0.5; max_v += 0.5 }
    const value_range = max_v - min_v
    min_v -= value_range * 0.1; max_v += value_range * 0.1

    const max_t = this.clip.duration > 0 ? this.clip.duration : (track.times[n - 1] || 1)
    const plot_w = CANVAS_W - PADDING_L - PADDING_R
    const plot_h = CANVAS_H - PADDING_T - PADDING_B

    // Axes
    ctx.strokeStyle = '#444'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(PADDING_L, PADDING_T)
    ctx.lineTo(PADDING_L, PADDING_T + plot_h)
    ctx.lineTo(PADDING_L + plot_w, PADDING_T + plot_h)
    ctx.stroke()

    // Y-axis labels (min, mid, max)
    ctx.fillStyle = '#aaa'
    ctx.font = '11px monospace'
    ctx.textAlign = 'right'
    for (let i = 0; i <= 4; i++) {
      const v = min_v + ((max_v - min_v) * i) / 4
      const y = PADDING_T + plot_h - (i / 4) * plot_h
      ctx.fillText(v.toFixed(2), PADDING_L - 6, y + 4)
      ctx.strokeStyle = '#2a2a2a'
      ctx.beginPath(); ctx.moveTo(PADDING_L, y); ctx.lineTo(PADDING_L + plot_w, y); ctx.stroke()
    }
    // X-axis labels
    ctx.textAlign = 'center'
    for (let i = 0; i <= 4; i++) {
      const t = (max_t * i) / 4
      const x = PADDING_L + (i / 4) * plot_w
      ctx.fillText(t.toFixed(2) + 's', x, PADDING_T + plot_h + 16)
    }

    // Compute plot points.
    this.cached_points = []
    for (let i = 0; i < n; i++) {
      const x = PADDING_L + (track.times[i] / max_t) * plot_w
      const y = PADDING_T + plot_h - ((values[i] - min_v) / (max_v - min_v)) * plot_h
      this.cached_points.push({ x, y, time: track.times[i], value: values[i], index: i })
    }

    // Curve.
    const interp = (track as KeyframeTrack & { getInterpolation?: () => number }).getInterpolation?.()
    ctx.strokeStyle = '#5af'
    ctx.lineWidth = 2
    ctx.beginPath()
    if (interp === InterpolateDiscrete) {
      // Step: horizontal then vertical.
      for (let i = 0; i < this.cached_points.length; i++) {
        const p = this.cached_points[i]
        if (i === 0) ctx.moveTo(p.x, p.y)
        else {
          const prev = this.cached_points[i - 1]
          ctx.lineTo(p.x, prev.y)
          ctx.lineTo(p.x, p.y)
        }
      }
    } else {
      for (let i = 0; i < this.cached_points.length; i++) {
        const p = this.cached_points[i]
        if (i === 0) ctx.moveTo(p.x, p.y)
        else ctx.lineTo(p.x, p.y)
      }
    }
    ctx.stroke()

    // Keyframe handles.
    for (const p of this.cached_points) {
      ctx.fillStyle = this.dragging_point_index === p.index ? '#fff' : '#ffcc00'
      ctx.beginPath()
      ctx.arc(p.x, p.y, HANDLE_RADIUS, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  private handle_pointer_down (e: PointerEvent): void {
    const rect = this.canvas?.getBoundingClientRect()
    if (rect === undefined) return
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    for (const p of this.cached_points) {
      const dx = p.x - x; const dy = p.y - y
      if (dx * dx + dy * dy <= (HANDLE_RADIUS + 4) * (HANDLE_RADIUS + 4)) {
        this.dragging_point_index = p.index
        this.canvas?.setPointerCapture(e.pointerId)
        this.render()
        return
      }
    }
  }

  private handle_pointer_move (e: PointerEvent): void {
    if (this.dragging_point_index === null) return
    const track = this.get_current_track()
    if (track === null || this.canvas === null) return
    const size = track.getValueSize()
    const rect = this.canvas.getBoundingClientRect()
    const y = e.clientY - rect.top

    // Convert pixel y back to value.
    const point = this.cached_points[this.dragging_point_index]
    if (point === undefined) return
    // Recompute current value range to keep mapping consistent.
    const values: number[] = []
    for (let i = 0; i < track.times.length; i++) values.push(track.values[i * size + this.current_component])
    let min_v = Infinity; let max_v = -Infinity
    for (const v of values) { if (v < min_v) min_v = v; if (v > max_v) max_v = v }
    if (min_v === max_v) { min_v -= 0.5; max_v += 0.5 }
    const value_range = max_v - min_v
    min_v -= value_range * 0.1; max_v += value_range * 0.1
    const plot_h = CANVAS_H - PADDING_T - PADDING_B
    const new_value = min_v + ((PADDING_T + plot_h - y) / plot_h) * (max_v - min_v)

    // Write back into the track's values buffer.
    const writable = track.values as unknown as Float32Array | number[]
    writable[point.index * size + this.current_component] = new_value

    // For quaternion tracks, renormalize the touched keyframe so the rotation
    // stays valid. Without this, dragging one component breaks unit length
    // and the rest of the rotation chain produces NaNs / non-rotations.
    if (track instanceof QuaternionKeyframeTrack) {
      const i = point.index * 4
      let x_v = writable[i]
      let y_v = writable[i + 1]
      let z_v = writable[i + 2]
      let w_v = writable[i + 3]
      const len = Math.sqrt(x_v * x_v + y_v * y_v + z_v * z_v + w_v * w_v)
      if (len > 1e-9) {
        x_v /= len; y_v /= len; z_v /= len; w_v /= len
      }
      // Hemisphere consistency: if the renormalized quaternion is on the
      // opposite hemisphere from a neighboring keyframe, flip all four
      // components. q and -q are the same rotation, but Three.js's slerp
      // between adjacent tracks can take the long way around when they
      // disagree on hemisphere. Pick whichever neighbor exists (prefer
      // the one before the dragged keyframe).
      const neighbor_index = point.index > 0 ? point.index - 1
        : point.index + 1 < track.times.length ? point.index + 1 : -1
      if (neighbor_index >= 0) {
        const ni = neighbor_index * 4
        const dot = writable[ni] * x_v + writable[ni + 1] * y_v + writable[ni + 2] * z_v + writable[ni + 3] * w_v
        if (dot < 0) {
          x_v = -x_v; y_v = -y_v; z_v = -z_v; w_v = -w_v
        }
      }
      writable[i] = x_v
      writable[i + 1] = y_v
      writable[i + 2] = z_v
      writable[i + 3] = w_v
    }

    this.render()
  }

  private handle_pointer_up (e: PointerEvent): void {
    if (this.dragging_point_index === null) return
    this.dragging_point_index = null
    this.canvas?.releasePointerCapture(e.pointerId)
    this.on_change()
    this.render()
  }

  /**
   * Right-click handler: if the cursor is on an existing keyframe, prompt to
   * delete it. Otherwise compute the time + value at the cursor and insert a
   * new keyframe there. Mutates the underlying KeyframeTrack and triggers
   * `on_change` so the playback re-binds (the existing curves-editor close
   * path already does this; calling on_change here keeps live previews in
   * sync mid-session).
   */
  private handle_context_menu (e: MouseEvent): void {
    e.preventDefault()
    const rect = this.canvas?.getBoundingClientRect()
    if (rect === undefined) return
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    // Hit a keyframe?
    for (const p of this.cached_points) {
      const dx = p.x - cx; const dy = p.y - cy
      if (dx * dx + dy * dy <= (HANDLE_RADIUS + 4) * (HANDLE_RADIUS + 4)) {
        void confirm_action({
          title: 'Delete keyframe',
          message: `Delete keyframe at t=${p.time.toFixed(3)}s?`,
          confirm_label: 'Delete',
          danger: true
        }).then((confirmed) => {
          if (confirmed) this.delete_keyframe_at(p.index)
        })
        return
      }
    }
    // Empty area — insert a keyframe at the clicked time/value.
    this.insert_keyframe_at_pixel(cx, cy)
  }

  private delete_keyframe_at (index: number): void {
    const track = this.get_current_track()
    if (track === null) return
    if (track.times.length <= 1) {
      new ModalDialog('Cannot delete keyframe', 'Cannot delete the last keyframe - clear the track via the bone-track UI instead.').show()
      return
    }
    const size = track.getValueSize()
    const new_times = new Float32Array(track.times.length - 1)
    const new_values = new Float32Array((track.times.length - 1) * size)
    let dst = 0
    for (let i = 0; i < track.times.length; i++) {
      if (i === index) continue
      new_times[dst] = track.times[i]
      for (let k = 0; k < size; k++) new_values[dst * size + k] = track.values[i * size + k]
      dst++
    }
    ;(track as { times: Float32Array }).times = new_times
    ;(track as { values: Float32Array }).values = new_values
    this.on_change()
    this.render()
  }

  private insert_keyframe_at_pixel (cx: number, cy: number): void {
    const track = this.get_current_track()
    if (track === null || this.canvas === null) return
    const size = track.getValueSize()
    const n = track.times.length
    const max_t = this.clip.duration > 0 ? this.clip.duration : (track.times[n - 1] || 1)
    const plot_w = CANVAS_W - PADDING_L - PADDING_R
    const plot_h = CANVAS_H - PADDING_T - PADDING_B

    // Time from x. Clamp inside [0, max_t].
    const time = Math.max(0, Math.min(max_t, ((cx - PADDING_L) / plot_w) * max_t))

    // Sample the existing track at this time (linear interp) so the inserted
    // keyframe doesn't visually jump the curve. The clicked y value only sets
    // the chosen component; other components inherit the sampled values so
    // (e.g.) a quaternion track stays unit-length around the new keyframe.
    const sampled: number[] = sample_track_value(track, time, size)

    // Recompute the value range to convert pixel y back to a value (same
    // formula as render()).
    const values: number[] = []
    for (let i = 0; i < n; i++) values.push(track.values[i * size + this.current_component])
    let min_v = Infinity; let max_v = -Infinity
    for (const v of values) { if (v < min_v) min_v = v; if (v > max_v) max_v = v }
    if (min_v === max_v) { min_v -= 0.5; max_v += 0.5 }
    const value_range = max_v - min_v
    min_v -= value_range * 0.1; max_v += value_range * 0.1
    const new_value = min_v + ((PADDING_T + plot_h - cy) / plot_h) * (max_v - min_v)
    sampled[this.current_component] = new_value

    // Find the insertion index (first existing time strictly greater).
    let insert_at = n
    for (let i = 0; i < n; i++) {
      if (track.times[i] > time) { insert_at = i; break }
      if (track.times[i] === time) {
        // Replace existing keyframe value rather than inserting a duplicate
        // at the same time.
        for (let k = 0; k < size; k++) {
          ;(track.values as unknown as Float32Array)[i * size + k] = sampled[k]
        }
        this.on_change()
        this.render()
        return
      }
    }

    const new_times = new Float32Array(n + 1)
    const new_values = new Float32Array((n + 1) * size)
    for (let i = 0; i < insert_at; i++) {
      new_times[i] = track.times[i]
      for (let k = 0; k < size; k++) new_values[i * size + k] = track.values[i * size + k]
    }
    new_times[insert_at] = time
    for (let k = 0; k < size; k++) new_values[insert_at * size + k] = sampled[k]
    for (let i = insert_at; i < n; i++) {
      new_times[i + 1] = track.times[i]
      for (let k = 0; k < size; k++) new_values[(i + 1) * size + k] = track.values[i * size + k]
    }
    ;(track as { times: Float32Array }).times = new_times
    ;(track as { values: Float32Array }).values = new_values
    this.on_change()
    this.render()
  }
}

function sample_track_value (track: KeyframeTrack, t: number, item_size: number): number[] {
  const times = track.times
  const values = track.values
  if (times.length === 0) return new Array(item_size).fill(0)
  if (t <= times[0]) {
    return Array.from(values.slice(0, item_size))
  }
  const last = times.length - 1
  if (t >= times[last]) {
    const off = last * item_size
    return Array.from(values.slice(off, off + item_size))
  }
  let lo = 0; let hi = last
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (times[mid] <= t) lo = mid
    else hi = mid
  }
  const t0 = times[lo]; const t1 = times[hi]
  const alpha = t1 === t0 ? 0 : (t - t0) / (t1 - t0)
  const out: number[] = new Array(item_size)
  for (let k = 0; k < item_size; k++) {
    const v0 = values[lo * item_size + k]; const v1 = values[hi * item_size + k]
    out[k] = v0 + (v1 - v0) * alpha
  }
  return out
}
